/**
 * A scroll this app drives itself, frame by frame.
 *
 * `scrollTo({ behavior: "smooth" })` is the *browser's* animation, and the browser abandons it the
 * moment anything else writes the scroll offset or the scrolled content changes shape under it.
 * Both happen constantly in a transcript being followed: pages append, the turn holding the
 * playhead repaints on every frame of the clock, and a turn taller than the column is scrolled
 * through by hand. An abandoned smooth scroll is not a slower scroll — it is the column *arriving*
 * instead of travelling, which is exactly the jump following must never make. So the travel is
 * ours: a `motion` tween drives a 0→1 progress value and each frame writes `scrollTop` itself.
 *
 * **The destination is a function, not a number.** It is asked again on every frame, because where
 * the turn is has not settled while the column is moving towards it — a page landing above it
 * moves it down, and a re-render can change what is above it. Interpolating towards a number
 * captured before the first frame lands the column where the turn no longer is.
 *
 * **One travel per scroller.** A second `animateTo` stops the first and measures its own start
 * from wherever the column has actually reached, so a boundary follow arriving over a timeline
 * jump continues from what is on screen rather than restarting from where that jump began. The
 * tween itself starts fresh, so every travel takes the same time whatever interrupted it.
 */

import { animate } from "motion";

/** How long a travel takes, whatever the distance — far enough to read as motion, short enough not to wait. */
export const SCROLL_TRAVEL_SECONDS = 0.32;

/**
 * Where the column should end up, in the scroller's own coordinates.
 *
 * `null` means "nowhere measurable yet" — the turn is not mounted, or the scroller is gone — and
 * leaves the offset exactly where it is rather than sending the column to 0.
 */
export type ScrollDestination = () => number | null;

export interface AnimatedScroll {
  /** Travel to wherever the destination says, resolving when the column has arrived or been stopped. */
  animateTo: (destination: ScrollDestination) => Promise<void>;
  /** Put the column there now, with no travel at all. */
  jumpTo: (destination: ScrollDestination) => void;
  /** Abandon whatever is in flight, leaving the column where it had reached. */
  stop: () => void;
  /** A travel is in flight, so per-frame writes from elsewhere have to stand aside. */
  isAnimating: () => boolean;
}

export function createAnimatedScroll(resolveScroller: () => HTMLElement | null): AnimatedScroll {
  let inFlight: { stop: () => void } | null = null;

  const stop = () => {
    const running = inFlight;
    inFlight = null;
    running?.stop();
  };

  const jumpTo = (destination: ScrollDestination) => {
    stop();
    const scroller = resolveScroller();
    if (scroller === null) return;
    const top = destination();
    if (top === null) return;
    scroller.scrollTop = top;
  };

  const animateTo = (destination: ScrollDestination): Promise<void> => {
    // Stopping first is what makes a retarget continuous: `scrollTop` is then the offset the
    // abandoned travel had actually reached, not the one it set out from.
    stop();
    const scroller = resolveScroller();
    if (scroller === null || destination() === null) return Promise.resolve();
    const from = scroller.scrollTop;

    let arrive: () => void = () => undefined;
    const travel = new Promise<void>((resolve) => {
      arrive = resolve;
    });

    let controls: { stop: () => void } | null = null;
    const entry = {
      done: false,
      stop: () => {
        if (entry.done) return;
        entry.done = true;
        controls?.stop();
        arrive();
      },
    };

    controls = animate(0, 1, {
      duration: SCROLL_TRAVEL_SECONDS,
      ease: "easeOut",
      onUpdate: (progress: number) => {
        const top = destination();
        if (top === null) return;
        scroller.scrollTop = from + (top - from) * progress;
      },
      onComplete: () => {
        entry.done = true;
        if (inFlight === entry) inFlight = null;
        arrive();
      },
    });

    if (!entry.done) inFlight = entry;
    return travel;
  };

  return { animateTo, jumpTo, stop, isAnimating: () => inFlight !== null };
}
